<template>
  <form method="get" :action="formAction" class="live-search">
    <div class="live-search__input-fields">
      <div class="live-search__input-container">
        <base-sprite-icon
            width="410"
            height="50"
            class="live-search__input-background"
            icon="suchfeld_umrandung__input"
        />
        <input
            type="search"
            maxlength="100"
            required
            name="q"
            class="live-search__input-field"
            :placeholder=inputTitle
            :title=inputTitle
            aria-describedby="js-live-search-submit-button"
            autocomplete="off"
            v-model="searchValue"
            @focus="openFlyout"
        />
      </div>
      <div class="live-search__button-container">
        <base-sprite-icon
            width="50"
            height="50"
            class="live-search__button-background"
            icon="suchfeld_umrandung__button"
        />
        <button
            class="btn btn-primary live-search__button-field"
            id="js-live-search-submit-button"
            :title="buttonTitle"
        >
          <base-sprite-icon height="20" width="20" icon="icon_suche"/>
        </button>
      </div>
    </div>
    <div v-if="showFlyout" v-click-outside="closeFlyout" class="flyout bg-light border-dark container"
         :class="{flyout1: hasOneColumn, flyout2: hasTwoColumns}">
      <div class="row">
        <aside
            class="bg-et_blue_100 bg-opacity-25 py-2 mh-100"
            :class="{ 'col-12': !showCards, 'col-12 col-md-4': showCards }"
            v-if="showSideBar"
        >
          <ListSection :title="catHeader + ':'" v-if="hasCategories && result">
            <li v-for="category in result.categories" :key="category.id">
              <a
                  :href="category.url"
                  class="text-decoration-none text-dark highlight-em"
                  v-html="category.highlighted"
              >
              </a>
            </li>
          </ListSection>
          <ListSection :title="prevHeader + ':'" v-if="hasPrevious && result">
            <li
                v-for="formerSearch in result.previous"
                :key="formerSearch.highlighted"
            >
              <a
                  :href="
                  '/suche/?q=' + encodeURIComponent(formerSearch.searchTerm)
                "
                  class="text-decoration-none text-dark highlight-em"
                  v-html="`${formerSearch.highlighted} (${formerSearch.results})`"
              >
              </a>
            </li>
          </ListSection>
          <ListSection :title="blogHeader + ':'" v-if="hasBlog && result">
            <li v-for="blogEntry in result.blog" :key="blogEntry.id">
              <a
                  :href="blogEntry.url"
                  class="text-decoration-none text-dark highlight-em"
                  v-html="blogEntry.highlighted"
              >
              </a>
            </li>
          </ListSection>
        </aside>
        <div
            :class="{ 'col-12': !showSideBar, 'col-md-8': showSideBar }"
            v-if="showCards"
        >
          <div class="row">
            <div
                class="pt-2"
                :class="{ 'col-12 col-lg-6': hasSets, 'col-12': !hasSets }"
                v-if="hasProducts"
            >
              <ListSection :title="productHeader + ':'" v-if="result && result.products">
                <ArtikelCard
                    v-for="(artikel, key) in result.products"
                    :key="key"
                    :artikel="artikel"
                    class="my-4"
                />
              </ListSection>
            </div>
            <div
                class="pt-2"
                :class="{
                'col-12 col-lg-6': hasProducts,
                'col-12': !hasProducts,
              }"
                v-if="hasSets"
            >
              <ListSection :title="setHeader + ':'" v-if="result && result.sets">
                <SetCard v-for="set in result.sets" :key="set.id" :set="set" class="my-4"/>
              </ListSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import {ref, type Ref, watch, computed} from "vue";
import BaseSpriteIcon from "@/components/ui/BaseSpriteIcon.vue";
import ListSection from "@/components/layout/LiveSearch/ListSection.vue";
import ArtikelCard from "@/components/layout/LiveSearch/ArtikelCard.vue";
import SetCard from "@/components/layout/LiveSearch/SetCard.vue";
import {type SearchResult} from "@/ts/components/Types/SearchResult";
import type EtDataType from "@/ts/components/Types/EtDataType";

// const etData: EtDataType = FrontendHandler.getPublicVar<EtDataType>("etData");
const etData: EtDataType = window.etData;
etData.liveSearchData.txtSchwierigkeit = decodeHtml(etData.liveSearchData.txtSchwierigkeit);
etData.liveSearchData.txtDauer = decodeHtml(etData.liveSearchData.txtDauer);
const formAction = etData.liveSearchData.formAction;
const inputTitle = decodeHtml(etData.liveSearchData.inputTitle);
const buttonTitle = decodeHtml(etData.liveSearchData.buttonTitle);
const catHeader = decodeHtml(etData.liveSearchData.catHeader);
const prevHeader = decodeHtml(etData.liveSearchData.prevHeader);
const blogHeader = decodeHtml(etData.liveSearchData.blogHeader);
const productHeader = decodeHtml(etData.liveSearchData.productHeader);
const setHeader = decodeHtml(etData.liveSearchData.setHeader);

const searchValue: Ref<string> = ref(decodeHtml(etData.liveSearchData.searchTerm));
const result: Ref<SearchResult | null> = ref(null);
const prev: Ref<SearchResult | null> = ref(null);
const forceCloseFlyout: Ref<boolean> = ref(false);

const hasCategories = computed(() => !!result.value?.categories.length);
const hasPrevious = computed(() => !!result.value?.previous.length);
const hasBlog = computed(() => !!result.value?.blog.length);
const hasProducts = computed(() => !!result.value?.products.length);
const hasSets = computed(() => !!result.value?.sets.length);
const hasOneColumn = computed(() => (hasCategories.value || hasPrevious.value || hasBlog.value) ? !hasProducts.value && !hasSets.value : hasProducts.value ? !hasSets.value : hasSets.value);
const hasTwoColumns = computed(() => (hasCategories.value || hasPrevious.value || hasBlog.value) ? hasProducts.value && !hasSets.value || !hasProducts.value && hasSets.value : hasProducts.value && hasSets.value);

const showSideBar = computed(
    () => hasCategories.value || hasPrevious.value || hasBlog.value,
);

const showCards = computed(() => hasProducts.value || hasSets.value);

const showFlyout = computed(
    () =>
        forceCloseFlyout.value ||
        (result.value && (result.value?.totalFound ?? 0) > 0),
);
let sequenceNo = 0;
let responseNo = 0;
let previousResult = {
  categories: [],
  previous: [],
  blog: [],
  products: [],
  searchTerm: "",
  sequenceNo: 0,
  sets: [],
  content: [],
  totalFound: 0,
  spellCheck: [],
} satisfies SearchResult;

function openFlyout(): void {
  if (prev?.value) {
    result.value = prev.value;
  }
}

function closeFlyout(event: Event): void {
  if (event?.target instanceof HTMLElement && !event.target.classList.contains('live-search__input-field')) {
    prev.value = result.value;
    result.value = null;
  }
}

function decodeHtml(html: string): string {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

let fetchSeverDataTimeoutId:any;

function fetchSeverData(body:any) {
  fetch(etData.searchEndpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })
      .then((response) => response.json())
      .then(function (data) {
        if (data.data.sequenceNo < responseNo) {
          result.value = previousResult;
          return;
        }
        if (data.statusCode !== 200) {
          console.error("live search failed, status: " + data.statusCode);
          result.value = null;
          return;
        }
        responseNo = data.data.sequenceNo;
        result.value = previousResult = data.data;
      })
      .catch(function () {
        result.value = null;
        console.error("live search failed");
      });
}

watch(searchValue, async (newSearchValue, _) => {
  if (newSearchValue === "") {
    result.value = null;
    return;
  }

  const body = {
    searchTerm: newSearchValue,
    shopId: etData.shopId,
    presenceId: etData.presenceId,
    languageCode: etData.lang,
    limits: [
      {
        type: "PRO",
        limit: 10,
      },
      {
        type: "CAT",
        limit: 10,
      },
      {
        type: "SET",
        limit: 10,
      },
      {
        type: "BLG",
        limit: 10,
      },
      {
        type: "PRV",
        limit: 5,
      },
    ],
    queryFields: [
      {
        name: "name",
        boost: 10,
      },
      {
        name: "searchKeywords",
        boost: 4,
      },
      {
        name: "brand",
        boost: 1,
      },
      {
        name: "itemNumber",
        boost: 1,
      },
      {
        name: "variantItemNumbers",
        boost: 1,
      },
      {
        name: "keywords",
        boost: 5,
      },
      {
        name: "productType",
        boost: 12,
      },
      {
        name: "categoryName",
        boost: 8,
      },
    ],
    returnCustomObjectFields: [
      {
        name: "difficulty",
        type: "int",
      },
      {
        name: "timeAndEffort",
        type: "int",
      },
      {
        name: "cancellation",
        type: "string",
        shopId: etData.shopId
      },
      {
        name: "deliveryStatus",
        type: "int",
        shopId: etData.shopId
      },
    ],
    sequenceNo: ++sequenceNo,
  };

  clearTimeout(fetchSeverDataTimeoutId);
  fetchSeverDataTimeoutId = setTimeout(() => fetchSeverData(body), 500);
});
</script>

<style scoped lang="scss">
.live-search {
  --height: var(--et-search-height, 50px);

  position: relative;
  width: 100%;
  max-width: 500px;

  &__input-fields {
    flex: 1 1 100%;
    display: grid;
    position: relative;
    grid-template-columns: auto var(--height);
    height: var(--height);
  }

  &__input-container {
    position: relative;
    min-width: 200px;
  }

  /**
   * Todo: warum zum xxx greifen position, inset und z-index hier nicht?s
   */
  &__button-field,
  &__input-field {
    appearance: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    box-sizing: border-box;
    //box-shadow: none;
    margin: 0;
    padding: 0 1rem;
    border: 0 none;
    border-radius: 0;
    outline: 0 none;
    background: transparent;
  }

  &__button-container {
    position: relative;
  }

  &__button-field {
    --icon-foreground-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__input-background,
  &__button-background {
    position: absolute;
    inset: 0;
    width: 100%;
    z-index: 1;
  }

  &__button-background {
    --icon-background-color: var(--bs-primary);
  }

  &:has(&__button-field:hover) &__button-background {
    opacity: 0.85;
  }
}

.flyout {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% - 5px);
  max-height: 80vh;
  min-width: 300px;
  width: calc(100vw - 1rem);
  z-index: 1;
  border: solid 1px;
  overflow-y: auto;
  overflow-x: hidden;

  &.flyout1 {
    max-width: 500px;
  }

  &.flyout2 {
    max-width: 800px;
  }
}
</style>
